import { env } from '@env'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { APP_NAME } from '@zet/end-points'
import { zetApi } from '@zet/network'

import { SUBSCRIPTION_STATUS } from '../../utils/constants'
import { LandingPageContentResponse, SubscriptionInfoResponse } from './types'

// Conditionally import EaseBuzz SDK only for non-MyTeams apps
let initializeEasebuzzCheckout: any
if (APP_NAME !== MY_TEAMS_ADVISOR) {
  try {
    const easebuzzModule = require('react-native-easebuzz-sdk')
    initializeEasebuzzCheckout = easebuzzModule.initializeEasebuzzCheckout
  } catch (error) {
    console.warn('EaseBuzz SDK not available')
  }
}

export async function getLandingPageContent(): Promise<LandingPageContentResponse> {
  const { data } = await zetApi.get<LandingPageContentResponse>(
    `/v1/pg/content?feature=ZET_PRIME`,
  )
  return data
}

export async function subscribeZetPrime(isRenewSubscription = false) {
  const { data } = await zetApi.post('/v1/pg/subscribe', {
    orderTag: 'ZET_PRIME',
    amount: 99.0,
    addons: null,
    isRenewSubscription,
    returnUrl: 'https://partner.zetapp.in',
  })
  return data
}

export async function getSubscriptionInfo(): Promise<SubscriptionInfoResponse> {
  const { data } = await zetApi.get<SubscriptionInfoResponse>(
    '/v1/pg/subscription-info?planName=ZET_PRIME',
  )
  return data
}

export const getSubscriptionStatus = async (orderId: string) => {
  const { data } = await zetApi.get(`/v1/pg/subscription/${orderId}/status`)
  return data
}

export const initiatePayment = async (accessKey: string) => {
  // MyTeams app should not use EaseBuzz payment
  if (APP_NAME === MY_TEAMS_ADVISOR) {
    console.warn('Payment not supported in MyTeams app')
    throw new Error('Payment not supported in MyTeams app')
  }

  try {
    if (!initializeEasebuzzCheckout) {
      throw new Error('EaseBuzz SDK not available')
    }
    const response = await initializeEasebuzzCheckout(
      accessKey,
      env?.EASEBUZZ_PAYMENT_MODE ?? 'production',
    )
    return response
  } catch (error) {
    console.error('Payment Failed:', error)
    throw error
  }
}

/**
 * Retry subscription status API call with exponential backoff
 * @param orderId - The order ID to check status for
 * @param getSubscriptionStatusFn - The mutation function to call
 * @param onSuccessCallback - Callback to execute on successful payment
 * @param attempt - Current attempt number (internal use)
 * @param maxAttempts - Maximum number of retry attempts (default: 3)
 * @param retryDelay - Delay between retries in milliseconds (default: 3000)
 * @returns Promise<void> - Resolves if payment is successful, rejects with error if all retries failed
 */
export const retrySubscriptionStatus = (
  orderId: string,
  getSubscriptionStatusFn: (
    orderId: string,
    options: {
      onSuccess: (data: any) => void
      onError: (error: any) => void
    },
  ) => void,
  onSuccessCallback: () => void,
  attempt = 1,
  maxAttempts = 20,
  retryDelay = 5000,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    getSubscriptionStatusFn(orderId, {
      onSuccess: (data) => {
        // Check if payment status is SUCCESS
        if (data?.paymentStatus === SUBSCRIPTION_STATUS.SUCCESS) {
          // Payment successful - execute callback and resolve
          onSuccessCallback()
          resolve()
        } else {
          // Payment not successful yet, retry
          if (data?.paymentStatus !== SUBSCRIPTION_STATUS.FAILED) {
            setTimeout(() => {
              retrySubscriptionStatus(
                orderId,
                getSubscriptionStatusFn,
                onSuccessCallback,
                attempt + 1,
                maxAttempts,
                retryDelay,
              )
                .then(resolve)
                .catch(reject)
            }, retryDelay)
          } else {
            // All retries exhausted and payment not successful
            reject(new Error('Something went wrong. Please try again.'))
          }
        }
      },
      onError: (error) => {
        // API call failed - don't retry, immediately reject
        reject(new Error('Something went wrong. Please try again.'))
      },
    })
  })
}
